/**
 * Calendar overview — 7-column grid showing each day and transit segment.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type {
  TravelItemResponse,
  TravelEventResponse,
  TravelTripResponse,
  TravelAttachmentResponse,
} from '../api';
import {
  formatDayDate,
  getCityColor,
  setCityColor,
  getEventTypeColor,
  setEventTypeColor,
  EVENT_TYPE_LABELS,
  TRANSIT_MODE_EMOJI,
  resetCityColors,
} from './TravelHelpers';
import { TravelAttachmentPreview } from './TravelAttachmentPreview';
import { formatTime } from './TravelSettings';
import type { TravelEventType } from '../api';

// =============================================================================
// Saved swatches — persisted in localStorage
// =============================================================================

const SWATCHES_STORAGE_KEY = 'fias-travel-saved-swatches';
const MAX_SWATCHES = 12;

function loadSwatches(): string[] {
  try {
    const raw = localStorage.getItem(SWATCHES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSwatches(swatches: string[]): void {
  try {
    localStorage.setItem(SWATCHES_STORAGE_KEY, JSON.stringify(swatches));
  } catch {
    // localStorage unavailable
  }
}

// =============================================================================
// ColorDotPicker — popover with native picker + swatch shelf
// =============================================================================

function ColorDotPicker({
  type,
  cityName,
  onColorChange,
}: {
  type?: TravelEventType;
  cityName?: string;
  onColorChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [swatches, setSwatches] = useState<string[]>(loadSwatches);
  const [selectedSwatchIdx, setSelectedSwatchIdx] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const color = cityName ? getCityColor(cityName) : getEventTypeColor(type!);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setOpen(false);
      setSelectedSwatchIdx(null);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSwatches(loadSwatches());
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, handleClickOutside]);

  const handleColorInput = (newColor: string) => {
    if (cityName) {
      setCityColor(cityName, newColor);
    } else {
      setEventTypeColor(type!, newColor);
    }
    onColorChange();

    // If a swatch is selected, update it in place
    if (selectedSwatchIdx !== null) {
      const updated = [...swatches];
      updated[selectedSwatchIdx] = newColor;
      setSwatches(updated);
      saveSwatches(updated);
    }
  };

  const handleSwatchClick = (idx: number) => {
    const swatchColor = swatches[idx];
    setSelectedSwatchIdx(idx);
    if (cityName) {
      setCityColor(cityName, swatchColor);
    } else {
      setEventTypeColor(type!, swatchColor);
    }
    onColorChange();
  };

  const handleAddSwatch = () => {
    if (swatches.length >= MAX_SWATCHES) return;
    const updated = [...swatches, color];
    setSwatches(updated);
    saveSwatches(updated);
    setSelectedSwatchIdx(updated.length - 1);
  };

  const handleRemoveSwatch = (idx: number) => {
    const updated = swatches.filter((_, i) => i !== idx);
    setSwatches(updated);
    saveSwatches(updated);
    if (selectedSwatchIdx === idx) setSelectedSwatchIdx(null);
    else if (selectedSwatchIdx !== null && selectedSwatchIdx > idx) {
      setSelectedSwatchIdx(selectedSwatchIdx - 1);
    }
  };

  return (
    <div className="relative inline-flex shrink-0" ref={popoverRef}>
      <button
        className="w-2.5 h-2.5 rounded-full cursor-pointer ring-1 ring-transparent hover:ring-foreground/30 transition-all"
        style={{ backgroundColor: color }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
          setSelectedSwatchIdx(null);
        }}
      />
      {open && (
        <div
          className="absolute z-50 top-5 left-1/2 -translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg p-2.5 w-44"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Native color picker — displayed large so the hue selector is visible */}
          <input
            type="color"
            value={color}
            onChange={(e) => handleColorInput(e.target.value)}
            className="w-full h-28 cursor-pointer border border-border rounded-md p-0 bg-transparent"
          />

          {/* Saved swatches */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-muted-foreground font-medium">Saved</span>
              {swatches.length < MAX_SWATCHES && (
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  onClick={handleAddSwatch}
                >
                  + Save current
                </button>
              )}
            </div>
            {swatches.length === 0 ? (
              <p className="text-[10px] text-muted-foreground/60">No saved colors</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {swatches.map((swatch, idx) => (
                  <button
                    key={idx}
                    className={`group/swatch relative w-5 h-5 rounded cursor-pointer transition-all ${
                      selectedSwatchIdx === idx
                        ? 'ring-2 ring-foreground ring-offset-1 ring-offset-background'
                        : 'ring-1 ring-border hover:ring-foreground/40'
                    }`}
                    style={{ backgroundColor: swatch }}
                    onClick={() => handleSwatchClick(idx)}
                  >
                    <span
                      className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-destructive text-destructive-foreground text-[8px] leading-3 text-center opacity-0 group-hover/swatch:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSwatch(idx);
                      }}
                    >
                      ×
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface TravelCalendarViewProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
  onSelectItem?: (itemId: string) => void;
  onSwitchToPlanner?: () => void;
  /** When true, clicks do not navigate to the planner (used on the public
   * shared-trip page where no editor exists). */
  readOnly?: boolean;
}

export function TravelCalendarView({
  trip,
  items,
  events,
  attachments = [],
  onSelectItem,
  onSwitchToPlanner,
  readOnly = false,
}: TravelCalendarViewProps) {
  // Force re-render when a color changes
  const [, setColorTick] = useState(0);
  const bumpColors = () => setColorTick((n) => n + 1);

  // Reset city colors to get consistent assignment
  resetCityColors();

  // Build events map (itemId -> events[]), sorted by time within each day.
  // Times are stored as "HH:MM" 24-hour strings, so lexicographic sort matches
  // chronological order. Events without a time fall to the bottom of the day —
  // they have no schedule anchor and shouldn't push timed entries out of order.
  const eventsByItem = new Map<string, TravelEventResponse[]>();
  for (const event of events) {
    const list = eventsByItem.get(event.itemId) || [];
    list.push(event);
    eventsByItem.set(event.itemId, list);
  }
  for (const list of eventsByItem.values()) {
    list.sort((a, b) => {
      if (a.time && b.time) return a.time.localeCompare(b.time);
      if (a.time) return -1;
      if (b.time) return 1;
      return 0;
    });
  }

  // Build attachments-by-event map
  const attachmentsByEvent = new Map<string, TravelAttachmentResponse[]>();
  for (const att of attachments) {
    if (!att.eventId) continue;
    const list = attachmentsByEvent.get(att.eventId) || [];
    list.push(att);
    attachmentsByEvent.set(att.eventId, list);
  }

  // Compute unique cities for legend (primary + additional locations)
  const cities = new Set<string>();
  items.forEach((item) => {
    if (item.city) cities.add(item.city);
    if (item.locations) {
      for (const loc of item.locations) {
        if (loc.city) cities.add(loc.city);
      }
    }
  });

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-3">
          {(Object.keys(EVENT_TYPE_LABELS) as TravelEventType[]).map((type) => (
            <span key={type} className="flex items-center gap-1.5">
              <ColorDotPicker type={type} onColorChange={bumpColors} />
              {EVENT_TYPE_LABELS[type]}
            </span>
          ))}
        </div>
        {cities.size > 0 && (
          <div className="flex flex-wrap gap-3 border-l border-border pl-4">
            {Array.from(cities).map((city) => (
              <span key={city} className="flex items-center gap-1.5">
                <ColorDotPicker cityName={city} onColorChange={bumpColors} />
                {city}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
        {items.map((item) => {
          if (item.itemType === 'transit') {
            return (
              <div
                key={item.itemId}
                onClick={() => {
                  if (readOnly) return;
                  onSelectItem?.(item.itemId);
                  onSwitchToPlanner?.();
                }}
                className="rounded-md border border-border bg-card cursor-pointer hover:-translate-y-px hover:shadow-sm transition-all overflow-hidden"
              >
                <div className="h-1.5 bg-[#5b7ff5]" />
                <div className="p-2 text-xs">
                  <div className="font-medium">
                    {item.transitMode && TRANSIT_MODE_EMOJI[item.transitMode]}{' '}
                    {item.transitMode || 'Transit'}
                  </div>
                  <div className="text-muted-foreground mt-0.5">
                    {item.transitFrom || '?'} → {item.transitTo || '?'}
                  </div>
                </div>
              </div>
            );
          }

          // Day cell
          const dayEvents = eventsByItem.get(item.itemId) || [];
          const cityColor = item.city ? getCityColor(item.city) : '#94a3b8';
          const maxEvents = 3;
          const overflow = dayEvents.length - maxEvents;

          return (
            <div
              key={item.itemId}
              onClick={() => {
                if (readOnly) return;
                onSelectItem?.(item.itemId);
                onSwitchToPlanner?.();
              }}
              className="group rounded-md border border-border bg-card cursor-pointer hover:-translate-y-px hover:shadow-sm transition-all overflow-hidden"
            >
              <div className="h-1.5" style={{ backgroundColor: cityColor }} />
              <div className="p-2">
                <div className="flex justify-between items-start">
                  <div className="font-semibold text-sm leading-tight">
                    {item.dayTitle || `Day ${item.dayNumber}`}
                  </div>
                  <div className="text-[10px] text-muted-foreground shrink-0 ml-1">
                    {formatDayDate(trip.startDate, item.dayNumber ?? 1)}
                  </div>
                </div>
                {item.city && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {item.city}
                    {item.locations && item.locations.length > 0 && (
                      <span className="opacity-60">
                        {' + '}
                        {item.locations
                          .filter((l) => l.city)
                          .map((l) => l.city)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                )}
                {item.accommodationName && (
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {'\ud83c\udfe8'} {item.accommodationName}
                  </div>
                )}

                {/* Event chips */}
                <div className="mt-1.5 space-y-0.5">
                  {dayEvents.slice(0, maxEvents).map((ev) => {
                    const evAtts = attachmentsByEvent.get(ev.eventId) || [];
                    return (
                      <div key={ev.eventId}>
                        <div className="flex items-center gap-1 text-[10px]">
                          <ColorDotPicker type={ev.eventType} onColorChange={bumpColors} />
                          <span className="truncate">{ev.title}</span>
                          {ev.time && (
                            <span className="text-muted-foreground shrink-0 ml-auto">
                              {formatTime(ev.time)}
                            </span>
                          )}
                        </div>
                        <TravelAttachmentPreview attachments={evAtts} compact />
                      </div>
                    );
                  })}
                  {overflow > 0 && (
                    <div className="text-[10px] text-muted-foreground">+{overflow} more</div>
                  )}
                </div>

                {/* Edit hint */}
                <div className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                  Edit →
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
